<template>
  <div>
    <component
      :is="resolveLayoutVariant"
      :class="`${user.darkMode ? 'dark-mode' : ''} skin-variant--${appSkinVariant}`"
    >
      <transition
        :name="appRouteTransition"
        mode="out-in"
        appear
      >
        <router-view
          :key="$route.fullPath"
        ></router-view>
      </transition>
      <notify></notify>
      <storage-warning-banner></storage-warning-banner>
      <!--
        TXP-806. Non-dismissible banner shown while writes to this device's storage are failing.
        It sits at z-index 10000, above the two snackbars above it (both 9999), so it can never be
        hidden behind them. It clears itself only when a write actually succeeds.
      -->
      <save-failure-banner></save-failure-banner>
      <ConfirmationDialog ref="confirm"></ConfirmationDialog>
    </component>
    <v-overlay
      :value="loading || syncing"
      opacity="0.8"
      z-index="500"
      class="text-center"
      style="text-align: center"
    >
      <v-progress-circular
        v-if="!syncing || !progressValueSet"
        indeterminate
        size="64"
        width="7"
        color="primary"
        class="load-sync-progress"
      ></v-progress-circular>
      <p
        v-if="!syncing || !progressValueSet"
        class="loading-message"
      >
        Loading...
      </p>

      <v-progress-circular
        v-if="syncing && progressValueSet"
        rotate="180"
        size="80"
        width="7"
        color="primary"
        :value="progressValue"
        class="load-sync-progress"
      >
        {{ progressValue }}%
      </v-progress-circular>
      <p
        v-if="syncing && progressValueSet"
        class="loading-message"
      >
        {{ syncingTxt ? syncingTxt : 'Communicating...'}}
      </p>
      <div
        v-if="syncing"
        class="sync-warning"
      >
        <p class="sync-warning-primary">
          <v-icon
            size="14"
            color="warning"
          >
            {{ icons.mdiAlert }}
          </v-icon>
          Don’t touch or navigate away from this screen.
        </p>
        <p class="sync-warning-secondary">
          Leaving Treasure X may interrupt the sync and cause missing data.
        </p>
      </div>
    </v-overlay>
  </div>
</template>

<script>
import useAppConfig from '@core/@app-config/useAppConfig'
import { useLayout } from '@core/layouts/composable/useLayout'
import { useRouter } from '@core/utils'
import { mdiAlert, mdiLogoutVariant } from '@mdi/js'
import { computed } from '@vue/composition-api'

// Layouts
import ConfirmationDialog from '@/components/elements/ConfirmationDialog.vue'
import Notify from '@/components/elements/Notify.vue'
import SaveFailureBanner from '@/components/elements/SaveFailureBanner.vue'
import StorageWarningBanner from '@/components/elements/StorageWarningBanner.vue'
import LayoutBlank from '@/layouts/Blank.vue'
import LayoutContent from '@/layouts/Content.vue'
import useDynamicVh from '@core/utils/useDynamicVh'
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'

export default {
  components: {
    LayoutContent,
    LayoutBlank,
    ConfirmationDialog,
    Notify,
    SaveFailureBanner,
    StorageWarningBanner,
  },
  data() {
    return {
      icons: { mdiAlert, mdiLogoutVariant },
    }
  },
  computed: {
    ...mapFields(['online', 'loading']),
    ...mapGetters('auth', ['user']),
    ...mapGetters('encounters', ['syncing', 'syncingPos', 'syncingCnt', 'syncingTxt']),
    progressValue() {
      return Math.max(Math.min(Math.round((this.syncingPos / this.syncingCnt) * 100), 100), 5)
    },
    progressValueSet() {
      return !Number.isNaN(this.progressValue)
    },
    isDecrypted() {
      return this.$store.state.isDecrypted === false && this.isLoggedIn()
    },
  },
  async mounted() {
    // Add an event listener to detect when the user goes offline
    window.addEventListener('load', () => {
      window.addEventListener('online', this.updateOnlineStatus)
      window.addEventListener('offline', this.updateOnlineStatus)
    })

    // Blocks closing tab, going back, or reloading browser if there's a filled out form.
    // Note: This isn't supported on iOS!
    window.addEventListener('beforeunload', e => {
      // Allow leaving from the login page without a warning
      if (this.$route.name !== 'login') {
        e.preventDefault()
        e.returnValue = ''
      }
    })

    // Set the current online status
    this.updateOnlineStatus()

    // Wait for auth to load
    this.$auth.load().finally(() => {
      this.loading = false
    })

    // Verify login credentials every minute
    setInterval(() => {
      const now = Math.floor(Date.now() / 1000)

      // console.log(`Verify Session: ${new Date().toTimeString().slice(0, 8)}`, now, this.$store.getters['auth/getSessionExpiration'], this.$store.getters['auth/getSessionExpiration'] - now)
      if (
        this.$authUser.user()
        && this.$store.getters['auth/getSessionExpiration'] > 0
        && now >= this.$store.getters['auth/getSessionExpiration']
      ) {
        // Session expired, logout
        this.$router.push({
          name: 'login',
          query: { notify: 'You\'ve been logged out due to inactivity.', color: 'error', timeout: -1 },
        }).catch(() => {})
      } else if (
        this.online
        && this.$authUser.user()
        && this.$store.getters['auth/getSessionExpiration'] > 0
        && this.$store.getters['auth/getSessionExpiration'] - now < 60 * 30 // 30 minute warning
      ) {
        // Session is about to expire, warn user
        this.$root.confirm({
          title: 'Stay Logged In?',
          body: 'Do you wish to stay logged in?',
          cancel: 'No, Logout',
          cancelColor: 'error',
          cancelIcon: this.icons.mdiLogoutVariant,
          confirm: 'Yes, Stay Logged In',
          logoutTimer: true,
        }).then(result => {
          if (result === true) {
            this.$store.dispatch('auth/refresh')
              .catch(error => {
                setTimeout(() => {
                  this.$store.dispatch('notify', { value: error , color: 'error' })
                }, 1000);
              })
          } else {
            this.$store.dispatch('auth/logout')
          }
        })
      }
    }, 60 * 60 * 2 * 1000) // once every 2 hours

    // 20250813 JMC: Disabled to avoid potential slowness for users
    // with slow or intermittent internet connections.
    // Look for new comments
    // setInterval(() => {
    //   if (this.online && this.$authUser.user()) {
    //     this.$store.dispatch('comments/loadComments')
    //   }
    // }, 15 * 60 * 1000) // Once every 15 minutes
  },
  updated() {
    // Set the confirm dialog on the $root so it can be called globally
    if (this.$refs.confirm?.open) this.$root.confirm = this.$refs.confirm.open
  },
  methods: {
    updateOnlineStatus() {
      this.online = window.navigator.onLine
    },
    isLoggedIn() {
      return this.$store.getters['auth/getSessionExpiration'] > 0
    },
  },
  setup() {
    const { route } = useRouter()
    const { appSkinVariant, appRouteTransition } = useAppConfig()

    const { handleBreakpointLayoutSwitch } = useLayout()
    handleBreakpointLayoutSwitch()

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === 'blank') return 'layout-blank'
      if (route.value.meta.layout === 'content') return 'layout-content'

      return null
    })

    useDynamicVh()

    return {
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
    }
  },
}
</script>

<style lang="scss">
.loading-message {
  padding-top: 1rem;
}
.sync-warning {
  max-width: 320px;
  margin: 0.75rem auto 0;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  .sync-warning-primary {
    margin-bottom: 0.25rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v-warning-base);
  }
  .sync-warning-secondary {
    margin-bottom: 0;
    font-size: 0.75rem;
    line-height: 1.3;
    color: rgba(255, 255, 255, 0.7);
  }
}
.load-sync-progress {
  > svg {
    animation-name: progress-rotate;
    animation-duration: 1.2s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }
  .v-progress-circular__overlay {
    stroke: var(--v-primary-base);
  }
}
@keyframes progress-rotate {
    from {transform: rotate(180deg);}
    to {transform: rotate(540deg);}
}
.v-progress-circular--indeterminate .v-progress-circular__overlay {
  animation-duration: 2.4s;
  animation-timing-function: linear;
}
@keyframes progress-circular-dash {
  0% {
    stroke-dasharray: 50, 200;
    stroke-dashoffset: 0px;
  }
  40% {
    stroke-dasharray: 110, 200;
    stroke-dashoffset: 0px;
  }
  70% {
    stroke-dasharray: 5, 200;
    stroke-dashoffset: -50px;
  }
  100% {
    stroke-dasharray: 50, 200;
    stroke-dashoffset: 0px;
  }
}

// Global wound type color coding
// Centralized CSS variables for wound practice type colors
/* Reintroduce distinct header/body background variables for better contrast */
:root {
  /* Base practice type brand colors */
  --wound-color: #1895e9; /* Wound */
  --derm-color: #009587; /* Dermatology */
  --gtube-color: #CF775B; /* G-Tube */
  /* Derived translucency backgrounds (darker header, lighter body) */
  --wound-header-bg: rgba(27, 152, 235, 0.1);
  --wound-body-bg: rgba(27, 152, 235, 0.3);
  --derm-header-bg: rgba(0, 149, 135, 0.1);
  --derm-body-bg: rgba(0, 149, 135, 0.3);
  --gtube-header-bg: rgba(207, 119, 91, 0.1);
  --gtube-body-bg: rgba(207, 119, 91, 0.3);
}

.text-bold {
  font-weight: bold !important;
}

.wound-treatments {
  /* Wound type (practice_type_id = 1) */
  .wound-type-1 {
    &.wound-header,
    &.v-card__actions { background-color: var(--wound-header-bg) !important; }
    .v-card__actions { background-color: var(--wound-header-bg) !important; }
    .wound-treatment-details,
    .wound-details .wound-container,
    &.wound-treatment-details,
    &.wound-details .wound-container { background-color: var(--wound-body-bg) !important; }
  }
  /* Dermatology type (practice_type_id = 6) */
  .wound-type-6 {
    &.wound-header,
    &.v-card__actions { background-color: var(--derm-header-bg) !important; }
    .v-card__actions { background-color: var(--derm-header-bg) !important; }
    .wound-treatment-details,
    .wound-details .wound-container,
    &.wound-treatment-details,
    &.wound-details .wound-container { background-color: var(--derm-body-bg) !important; }
  }
  /* G-Tube type (practice_type_id = 5) */
  .wound-type-5 {
    &.wound-header,
    &.v-card__actions { background-color: var(--gtube-header-bg) !important; }
    .v-card__actions { background-color: var(--gtube-header-bg) !important; }
    .wound-treatment-details,
    .wound-details .wound-container,
    &.wound-treatment-details,
    &.wound-details .wound-container { background-color: var(--gtube-body-bg) !important; }
  }
}
</style>
